import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';
import { GoatHealthLog, TreatmentType } from '../../types/api';

const GoatMedicineLogsPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [logs, setLogs] = useState<GoatHealthLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterTag, setFilterTag] = useState('');
  
  // Filters
  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };
  
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadLogs();
    }
  }, [selectedLocation?.id, businessType, startDate, endDate, filterTag]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ location_id: selectedLocation?.id || '' });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (filterTag) params.append('tag', filterTag);
      // Show all health logs (all involve medicine)
      
      const response = await apiClient.get(`/goat/health-logs?${params.toString()}`);
      setLogs(response.data || []);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load medicine logs'));
    } finally {
      setLoading(false);
    }
  };

  const getTreatmentTypeColor = (type: TreatmentType) => {
    switch (type) {
      case 'vaccination': return 'bg-blue-100 text-blue-800';
      case 'deworming': return 'bg-green-100 text-green-800';
      case 'treatment': return 'bg-red-100 text-red-800';
      case 'other': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const clearFilters = () => {
    const defaultDates = getDefaultDates();
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
    setFilterTag('');
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/goat/logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">💊 Medicine Logs</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
        <Link
          to="/goat/health/new"
          className="btn-touch bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Record Treatment</span>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="form-label">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Filter by Goat Tag</label>
            <input
              type="text"
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              className="form-input"
              placeholder="Enter tag number"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={clearFilters}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            Reset Filters
          </button>
          <button
            onClick={loadLogs}
            className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Logs List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Medicine Records ({logs.length})
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : logs.length > 0 ? (
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-semibold text-gray-900">Tag: {log.tag}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTreatmentTypeColor(log.treatment_type)}`}>
                        {log.treatment_type}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>Date: {new Date(log.health_date).toLocaleDateString()}</div>
                      {log.medicine && <div className="font-medium">Medicine: {log.medicine}</div>}
                      {log.dose && <div>Dose: {log.dose}</div>}
                      {log.notes && <div>Notes: {log.notes}</div>}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(log.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">💊</div>
            <p className="text-gray-500 mb-4">No medicine records found</p>
            <Link
              to="/goat/health/new"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Record First Treatment
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoatMedicineLogsPage;
